import { Alert, Skeleton, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Widget, WidgetDefinition, WidgetProps, WidgetType } from '../types';
import { parseDates } from '../utils';
import { isWidgetDataError, type WidgetDataState } from '../widgetData';
import { VictoryArea, VictoryBar, VictoryChart, VictoryLine, VictoryPie, VictoryTheme, VictoryAxis, VictoryLegend, VictoryTooltip, VictoryVoronoiContainer } from 'victory';
import { KpiWidget } from '../WebWidget/KpiWidget';
import { shapeKpiData } from '../WebWidget/kpiData';
import { componentsTestIds } from '@components/testids';

const widgetComponents: Partial<Record<WidgetType, any>> = {
    areaChart: ({ data, xAxis, yAxis, series, theme, animate, padding, domainPadding, ...props }: any ) => {
        const grouped = groupBySeries( data, series );
        const seriesNames = Object.keys( grouped );
        const nameColorMap: Record<string, string> = seriesNames.reduce(
            ( acc, name, idx ) => {
                acc[name] = getRandomColor( idx ); // same function you already have
                return acc;
            },
            {} as Record<string, string>
        );
        return ( <VictoryChart 
            theme={theme || VictoryTheme.material} 
            animate={animate} 
            padding={padding} 
            domainPadding={domainPadding}
            containerComponent={
                <VictoryVoronoiContainer
                    labels={({ datum }) => `${datum[series]}: ${ datum[yAxis]}`}
                    labelComponent={<VictoryTooltip cornerRadius={5} flyoutStyle={{ fill: 'white' }} />}
                />
            }
        >
        <VictoryAxis tickFormat={( t ) => t} />
        <VictoryAxis dependentAxis />
        {seriesNames.map(( name, idx ) => (
        <VictoryArea
            {...props}
             key={name}  
            data={grouped[name]}
            x={xAxis}
            y={yAxis}
            style={{
                data: {
                    fill: nameColorMap[name],
                    stroke: nameColorMap[name],
                    strokeWidth: 2,
                    fillOpacity: 0.3     
                }
            }}
        />
        ))}
        {props.legend && (
            <VictoryLegend
            x={50}
            y={10}
            centerTitle
            orientation="horizontal"
            data={seriesNames.map(( name, idx ) => ({
                name,
                symbol: { fill: nameColorMap[name] }
            }))}
            />
        )}
        </VictoryChart>
    );
    },
    barChart: ({ data, xAxis, yAxis, series, theme, animate, padding, domainPadding, horizontal, ...props }: any ) => (
        <VictoryChart 
            theme={theme || VictoryTheme.material} 
            animate={animate} 
            padding={padding} 
            domainPadding={domainPadding}
            containerComponent={
                <VictoryVoronoiContainer
                    labels={({ datum }) => `${datum[xAxis]}: ${datum[yAxis]}`}
                    labelComponent={<VictoryTooltip cornerRadius={5} flyoutStyle={{ fill: 'white' }} />}
                />
            }
        >
            <VictoryAxis tickFormat={( t ) => t} />
            <VictoryAxis dependentAxis />
            <VictoryBar 
                data={data} 
                x={xAxis} 
                y={yAxis} 
                horizontal={horizontal} 
                {...props} 
            />
            {props.legend && (
                <VictoryLegend 
                    x={50} 
                    y={10} 
                    centerTitle 
                    orientation="horizontal" 
                    data={[{ name: yAxis, symbol: { fill: props.style?.data?.fill || '#c43a31' }}]} 
                />
            )}
        </VictoryChart>
    ),
    lineChart: ({ data, xAxis, yAxis, series, theme, animate, padding, domainPadding, ...props }: any ) => {
        const grouped = groupBySeries( data, series );
        const seriesNames = Object.keys( grouped );

        const nameColorMap: Record<string, string> = seriesNames.reduce(
            ( acc, name, idx ) => {
            acc[name] = getRandomColor( idx ); 
            return acc;
            },
            {} as Record<string, string>
        );

        return (
            <VictoryChart
            theme={theme || VictoryTheme.material}
            animate={animate}
            padding={padding}
            domainPadding={domainPadding}
            containerComponent={
                <VictoryVoronoiContainer
                labels={({ datum }) => `${datum[series]}: ${datum[yAxis]}`}
                labelComponent={
                    <VictoryTooltip cornerRadius={5} flyoutStyle={{ fill: 'white' }} />
                }
                />
            }
            >
            <VictoryAxis tickFormat={( t ) => t} />
            <VictoryAxis dependentAxis />

            {seriesNames.map(( name ) => (
                <VictoryLine
                key={name}
                {...props}
                data={grouped[name]}
                x={xAxis}
                y={yAxis}
                style={{
                    data: {
                    stroke: nameColorMap[name],
                    strokeWidth: 2
                    }
                }}
                />
            ))}

            {props.legend && (
                <VictoryLegend
                x={50}
                y={10}
                centerTitle
                orientation="horizontal"
                data={seriesNames.map(( name ) => ({
                    name,
                    symbol: { fill: nameColorMap[name] }
                }))}
                />
            )}
            </VictoryChart>
        );
        },
    pieChart: ({ data, groupBy, valueMetric, theme, animate, padding, ...props }: any ) => (
        <VictoryPie 
            data={data} 
            x={groupBy} 
            y={valueMetric} 
            theme={theme || VictoryTheme.material} 
            animate={animate} 
            padding={padding}
            labels={({ datum }) => `${datum[groupBy]}: ${datum[valueMetric]}`}
            labelComponent={props.withLabels ? undefined : <VictoryTooltip />}
            {...props} 
        />
    ),
    donutChart: ({ data, groupBy, valueMetric, theme, animate, padding, ...props }: any ) => (
        <VictoryPie
            data={data}
            x={groupBy}
            y={valueMetric}
            theme={theme || VictoryTheme.material}
            animate={animate}
            padding={padding}
            innerRadius={70}
            labelComponent={props.withLabels ? undefined : <></>}
            {...props}
        />
    ),
    // TV1-1930 (S15) — the KPI is the one widget that renders IDENTICALLY on both
    // devices, so the web component is reused verbatim rather than reimplemented
    // in Victory. There is no chart here: it is a number, a delta and an arrow,
    // built from plain Mantine primitives that are already responsive. A second
    // implementation would be a second place for the unknown-vs-zero rules to rot.
    kpi: KpiWidget
};

const parseAreaChartData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
    if ( !Array.isArray( data )) return data;
    
    if ( widgetComponentProps?.series && widgetComponentProps?.xAxis && widgetComponentProps?.yAxis ) {
        const seriesField = widgetComponentProps.series;
        const xAxisField = widgetComponentProps.xAxis;
        const yAxisField = widgetComponentProps.yAxis;
        
        // For Victory, we need a simpler data format
        const formattedData = data.map( item => ({
            [xAxisField]: item[xAxisField],
            [seriesField]: item[seriesField],
            [yAxisField]: parseFloat( item[yAxisField]) || 0
        }));
        
        return parseDates( formattedData, widgetDefinition.dateRange );
    }
    return parseDates( data, widgetDefinition.dateRange );
};

const parseBarChartData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
    if ( !Array.isArray( data )) return data;
    
    if ( widgetComponentProps?.xAxis && widgetComponentProps?.yAxis ) {
        const xAxisField = widgetComponentProps.xAxis;
        const yAxisField = widgetComponentProps.yAxis;
        
        // For Victory, we need a simpler data format
        const formattedData = data.map( item => ({
            [xAxisField]: item[xAxisField],
            [yAxisField]: parseFloat( item[yAxisField]) || 0
        }));
        
        return parseDates( formattedData, widgetDefinition.dateRange );
    }
    return parseDates( data, widgetDefinition.dateRange );
};

const parseLineChartData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
   if ( !Array.isArray( data )) return data;
    
    if ( widgetComponentProps?.series && widgetComponentProps?.xAxis && widgetComponentProps?.yAxis ) {
        const seriesField = widgetComponentProps.series;
        const xAxisField = widgetComponentProps.xAxis;
        const yAxisField = widgetComponentProps.yAxis;
        
        // For Victory, we need a simpler data format
        const formattedData = data.map( item => ({
            [xAxisField]: item[xAxisField],
            [seriesField]: item[seriesField],
            [yAxisField]: parseFloat( item[yAxisField]) || 0
        }));
        
        return parseDates( formattedData, widgetDefinition.dateRange );
    }
    return parseDates( data, widgetDefinition.dateRange );
};

const parsePieChartData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
    if ( !Array.isArray( data )) return data;
    if ( widgetComponentProps?.groupBy && widgetComponentProps?.valueMetric ) {
        const groupByField = widgetComponentProps.groupBy;
        const metricField = widgetComponentProps.valueMetric;
        return data.map(( item, index ) => ({
            [groupByField]: item[groupByField],
            [metricField]: parseFloat( item[metricField]) || 0,
            color: getRandomColor( index )
        }));
    }
    return parseDates( data, widgetDefinition.dateRange );
};

const parseDonutChartData = ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => {
    return parsePieChartData( data, widgetComponentProps, widgetDefinition );
};

// TV1-1930 (S15) — identical to the web parser (and deliberately delegating to
// the SAME `shapeKpiData`): the unknown-vs-zero semantics must not be able to
// diverge between devices.
const parseKpiData = ( data: any, _widgetComponentProps: any, widgetDefinition: WidgetDefinition ) =>
    shapeKpiData( data, widgetDefinition?.measures?.[0] ?? '' );

const widgetParsers:Partial<Record<WidgetType, ( data: any, widgetComponentProps: any, widgetDefinition: WidgetDefinition ) => any>> = {
    areaChart: parseAreaChartData,
    barChart: parseBarChartData,
    lineChart: parseLineChartData,
    pieChart: parsePieChartData,
    donutChart: parseDonutChartData,
    kpi: parseKpiData
};

// colors for series
const colors: string[] = [
    '#8b46ff', '#2d7ff9', '#4caf50', '#ff9800', '#9c27b0', 
    '#607d8b', '#e91e63', '#3f51b5', '#009688', '#ffc107'
];

const getRandomColor = ( index: number ) => {
    return colors[index % colors.length];
};
function groupBySeries< T extends Record<string, any>, K extends keyof T & string >( data: T[], seriesKey: K ) {
  return data.reduce<Record<string, T[]>>(( acc, item ) => {
    const key = item[seriesKey];
    ( acc[key] ||= []).push( item );
    return acc;
  }, {});
}

const widgetPropsBuilders: Partial<Record<WidgetType, ( data: any, widgetComponentProps: any ) => any>> = {
    areaChart: ( data: any, widgetComponentProps: any ) => {
        const defaultProps = {
            animate: { duration: 500, onLoad: { duration: 500 }},
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            domainPadding: { x: [ 20, 20 ], y: [ 20, 20 ]},
            theme: VictoryTheme.material,
            style: {
                data: {
                    fill: getRandomColor( 0 ),
                    fillOpacity: 0.7,
                    stroke: getRandomColor( 0 ),
                    strokeWidth: 2
                }
            },
            legend: true
        };
        
        const mergedProps = { 
            ...defaultProps,
            data,
            xAxis: widgetComponentProps.xAxis,
            yAxis: widgetComponentProps.yAxis,
            series: widgetComponentProps.series
        };
        
        return mergedProps;
    },
    barChart: ( data: any, widgetComponentProps: any ) => {
        const defaultProps = {
            animate: { duration: 500, onLoad: { duration: 500 }},
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            domainPadding: { x: [ 20, 20 ], y: [ 20, 20 ]},
            theme: VictoryTheme.material,
            style: {
                data: {
                    fill: getRandomColor( 0 ),
                    width: 15
                }
            },
            horizontal: widgetComponentProps.orientation === 'horizontal',
            legend: true
        };
        
        const mergedProps = { 
            ...defaultProps,
            data,
            xAxis: widgetComponentProps.xAxis,
            yAxis: widgetComponentProps.yAxis
        };
        
        return mergedProps;
    },
    lineChart: ( data: any, widgetComponentProps: any ) => {
        const defaultProps = {
            animate: { duration: 500, onLoad: { duration: 500 }},
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            domainPadding: { x: [ 20, 20 ], y: [ 20, 20 ]},
            theme: VictoryTheme.material,
            style: {
                data: {
                    stroke: getRandomColor( 0 ),
                    strokeWidth: 2
                }
            },
            legend: true
        };
        
        const mergedProps = { 
            ...defaultProps,
            data,
            xAxis: widgetComponentProps.xAxis,
            yAxis: widgetComponentProps.yAxis,
            series: widgetComponentProps.series
        };
        
        return mergedProps;
    },
    pieChart: ( data: any, widgetComponentProps: any ) => {
        const defaultProps = {
            animate: { duration: 500, onLoad: { duration: 500 }},
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            theme: VictoryTheme.material,
            colorScale: colors,
            withLabels: widgetComponentProps.withLabels !== false,
            labelRadius: ({ radius }: any ) => radius - 40
        };
        
        const mergedProps = { 
            ...defaultProps,
            data,
            groupBy: widgetComponentProps.groupBy,
            valueMetric: widgetComponentProps.valueMetric
        };
        
        return mergedProps;
    },
    donutChart: ( data: any, widgetComponentProps: any ) => {
        const defaultProps = {
            animate: { duration: 500, onLoad: { duration: 500 }},
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            theme: VictoryTheme.material,
            colorScale: colors,
            withLabels: widgetComponentProps.withLabels !== false,
            labelRadius: ({ radius }: any ) => radius - 40
        };

        const mergedProps = {
            ...defaultProps,
            data,
            groupBy: widgetComponentProps.groupBy,
            valueMetric: widgetComponentProps.valueMetric
        };

        return mergedProps;
    },
    // As on web: pass the shaped value/delta through plus the display props.
    // `goodDirection` is left undefined so `KpiWidget` applies its `'neutral'`
    // default — colour stays opt-in on mobile too.
    kpi: ( parsed: any, widgetComponentProps: any ) => ({
        value: parsed?.value ?? null,
        delta: parsed?.delta ?? { kind: 'unknown' },
        hasCompare: Boolean( parsed?.hasCompare ),
        format: widgetComponentProps?.format,
        currency: widgetComponentProps?.currency,
        decimals: widgetComponentProps?.decimals,
        goodDirection: widgetComponentProps?.goodDirection,
        caption: widgetComponentProps?.caption
    })
};

// PREFACTOR-grammar — the mobile twin of WebWidget's error tile. It reads the SAME
// `widgetDataMap`, so it must understand the same discriminated outcome; otherwise
// it would hand the `{ status, data }` envelope straight to a Victory chart.
const MobileWidgetErrorTile = ({ error }: { error: string }) => (
    <Alert
        variant="light"
        color="red"
        h="100%"
        icon={<IconAlertTriangle size={16} />}
        data-testid={componentsTestIds.widgetError}
        styles={{ root: { overflow: 'auto' }}}
    >
        <Text size="xs" data-testid={componentsTestIds.widgetErrorMessage}>{error}</Text>
    </Alert>
);

export const MobileWidget = ({ data, widget, type, widgetComponentProps }: WidgetProps ) => {
    if ( data === undefined || data === null ) {
         return <Skeleton height={'100%'}/>;
    }

    if ( isWidgetDataError( data as WidgetDataState )) {
        return <MobileWidgetErrorTile error={( data as { status: 'error'; error: string }).error} />;
    }

    const payload = ( data as WidgetDataState )?.status === 'ok'
        ? ( data as { status: 'ok'; data: unknown }).data
        : data;

    const WidgetComponent = widgetComponents[type];
    const parseData = widgetParsers[type];
    const parsedData = parseData ? parseData( payload, widgetComponentProps, widget.definition ) : payload;
    const buildProps = widgetPropsBuilders[type];
    const widgetProps = buildProps ? buildProps( parsedData, widgetComponentProps ) : { data: parsedData };
    return <WidgetComponent {...widgetProps} />;
};
